import { useRef, useState } from "react";
import { GripVertical, Plus, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import {
  blankIngredient,
  encodeRows,
  ingredientsToValue,
  moveRow,
  renumber,
  sortByTypedOrder,
  type IngredientDraft,
} from "../ingredients";

/**
 * The ingredient list, authored as ROWS.
 *
 * THE RULE THIS COMPONENT EXISTS TO KEEP (KAL-355, HARD): a compound fact is
 * never authored through a JSON textarea, and never through the scalar "New
 * value" box either. `nutrition.active_ingredients` is a list of named amounts
 * in a declared order, so it is edited as a list of named amounts in a declared
 * order — one row per ingredient, with its name, its position and its
 * milligrams as three separate fields.
 *
 * What it replaced was worse than the raw JSON Damien saw on the LIVE NOW card.
 * The single textbox fed `draftValueFor`, which coerces a non-numeric entry to
 * `{ s: <the typed string> }` — so typing an ingredient list did not fail, it
 * SAVED, as a string, under a key the store holds as an object. Nothing on the
 * screen said so.
 *
 * REORDERING IS NOT MOUSE-ONLY. The handle is a real button: it drags, and it
 * also moves the row with the arrow keys. A drag-only list cannot be operated
 * from the keyboard at all, and the declared order is what prints on the pack —
 * it is not decoration that a keyboard user may skip.
 */
export function IngredientsField({
  id,
  helpId,
  label,
  rows,
  error,
  help,
  onChange,
  onCommit,
}: {
  id: string;
  helpId: string;
  label: string;
  rows: IngredientDraft[];
  /** The list is not a shape that can be saved. Shown INSTEAD of `help`. */
  error: string | null;
  help: string;
  onChange: (next: IngredientDraft[]) => void;
  /**
   * Finished editing — save the draft. The list's equivalent of a field blur.
   *
   * It is handed the rows it should save. A drag, a delete and a re-sort all
   * change the list and commit in the SAME event, and React has not re-rendered
   * by then — a commit that read the `rows` prop there would draft the edit
   * before last. Reading them from the argument is what keeps the draft and the
   * screen the same list.
   */
  onCommit: (rows: IngredientDraft[]) => void;
}) {
  // Which row is being dragged, so the others can show where it would land.
  const [dragging, setDragging] = useState<number | null>(null);

  // The one box the refusal names, re-derived from the same validator that
  // produced `error` — so the outline and the sentence cannot disagree.
  const verdict = error ? ingredientsToValue(rows) : null;
  const invalid = verdict && "error" in verdict ? verdict.at : undefined;
  const isInvalid = (index: number, field: "name" | "amountMg" | "order") =>
    invalid?.index === index && invalid.field === field;

  // The list as it stood when a box took focus. Leaving the box saves only if
  // the list is now different: every blur used to commit, so tabbing across
  // the rows drafted on each step — and a re-sort that changed nothing still
  // pushed a "new" list into the pane.
  const atFocus = useRef<string | null>(null);
  const remember = () => {
    atFocus.current = encodeRows(rows);
  };
  const settle = (next: IngredientDraft[]) => {
    const before = atFocus.current;
    atFocus.current = null;
    if (before !== null && encodeRows(next) === before) return;
    if (next !== rows) onChange(next);
    onCommit(next);
  };

  const change = (index: number, patch: Partial<IngredientDraft>) => {
    onChange(rows.map((row, at) => (at === index ? { ...row, ...patch } : row)));
  };

  const move = (from: number, to: number) => {
    if (to < 0 || to >= rows.length) return;
    const next = moveRow(rows, from, to);
    onChange(next);
    onCommit(next);
  };

  return (
    <div>
      {/* A group of fields, not one field — so the label names the GROUP and
          each row's inputs carry their own labels for a screen reader. */}
      <div role="group" aria-labelledby={`${id}-label`} aria-describedby={helpId}>
        <Label id={`${id}-label`} asChild>
          <span>{label}</span>
        </Label>

        <div className="mt-1 overflow-hidden rounded-ks-md border border-ks-grey-4">
          {/* Column headings, so the three boxes are not three anonymous
              inputs. "Amount" is captioned mg once here rather than repeated
              as placeholder text in every row. */}
          {/* The two icon columns get EMPTY cells, not `sr-only` labels:
              `sr-only` is `position: absolute`, which takes the element out of
              the grid, so "Name" fell into the 28px handle track and printed
              on top of "Order". Each row's buttons carry their own labels. */}
          <div
            aria-hidden
            className="grid grid-cols-[28px_minmax(0,1fr)_72px_112px_28px] items-center gap-2 border-b border-ks-grey-4 bg-ks-purple-4 px-2 py-1.5 text-[11px] font-bold tracking-wider whitespace-nowrap text-ks-grey-1 uppercase"
          >
            <span />
            <span>Name</span>
            <span>Order</span>
            <span>Amount (mg)</span>
            <span />
          </div>

          {rows.length === 0 ? (
            // Never a blank panel: an empty list is a state with a next step,
            // and the button below is it.
            <p className="px-3 py-4 text-sm text-ks-grey-1">
              No ingredients yet. Add the first one below.
            </p>
          ) : (
            rows.map((row, index) => (
              <div
                key={row.key}
                draggable
                onDragStart={(event) => {
                  setDragging(index);
                  event.dataTransfer.effectAllowed = "move";
                }}
                onDragOver={(event) => {
                  // Without this the drop never fires — the browser's default
                  // is to refuse the drop, silently.
                  event.preventDefault();
                  event.dataTransfer.dropEffect = "move";
                }}
                onDrop={(event) => {
                  event.preventDefault();
                  if (dragging !== null) move(dragging, index);
                  setDragging(null);
                }}
                onDragEnd={() => setDragging(null)}
                className={`grid grid-cols-[28px_minmax(0,1fr)_72px_112px_28px] items-center gap-2 border-b border-ks-grey-4 px-2 py-2 last:border-b-0 ${
                  dragging === index ? "bg-ks-purple-4" : "bg-ks-surface"
                }`}
              >
                <button
                  type="button"
                  // Reordering reads a different order; it publishes nothing on
                  // its own, so the shell's permission gate leaves it live for
                  // a read-only viewer exactly as it does the product picker.
                  aria-label={`Move ${row.name || `row ${index + 1}`}. Use the up and down arrow keys.`}
                  className="flex h-7 w-7 cursor-grab items-center justify-center rounded text-ks-grey-1 hover:bg-ks-grey-4 hover:text-ks-ink focus-visible:ring-2 focus-visible:ring-ks-purple focus-visible:outline-none"
                  onKeyDown={(event) => {
                    if (event.key === "ArrowUp") {
                      event.preventDefault();
                      move(index, index - 1);
                    } else if (event.key === "ArrowDown") {
                      event.preventDefault();
                      move(index, index + 1);
                    }
                  }}
                >
                  <GripVertical aria-hidden className="size-4" />
                </button>

                <Input
                  className="h-9"
                  aria-label={`Ingredient ${index + 1} name`}
                  aria-invalid={isInvalid(index, "name") || undefined}
                  value={row.name}
                  onChange={(event) => change(index, { name: event.target.value })}
                  onFocus={remember}
                  onBlur={() => settle(rows)}
                />

                <Input
                  className="h-9"
                  inputMode="numeric"
                  aria-label={`Ingredient ${index + 1} position in the list`}
                  aria-invalid={isInvalid(index, "order") || undefined}
                  value={row.order}
                  onChange={(event) => change(index, { order: event.target.value })}
                  onFocus={remember}
                  // Typing a position is a request to MOVE THIS row. Re-sorting
                  // on blur rather than per keystroke is what keeps the row
                  // editable — a list that jumps mid-digit cannot be typed in.
                  onBlur={() => settle(sortByTypedOrder(rows, index))}
                />

                <Input
                  className="h-9"
                  inputMode="decimal"
                  aria-label={`Ingredient ${index + 1} amount in milligrams`}
                  aria-invalid={isInvalid(index, "amountMg") || undefined}
                  value={row.amountMg}
                  onChange={(event) => change(index, { amountMg: event.target.value })}
                  onFocus={remember}
                  onBlur={() => settle(rows)}
                />

                <button
                  type="button"
                  aria-label={`Remove ${row.name || `ingredient ${index + 1}`}`}
                  className="flex h-7 w-7 items-center justify-center rounded text-ks-grey-1 hover:bg-ks-grey-4 hover:text-ks-danger focus-visible:ring-2 focus-visible:ring-ks-purple focus-visible:outline-none"
                  onClick={() => {
                    // Renumbered on the way out: deleting row 2 of 4 must leave
                    // 1, 2, 3 and not 1, 3, 4 — the gap would publish as a
                    // declared order with a hole in it.
                    const next = renumber(rows.filter((_, at) => at !== index));
                    onChange(next);
                    onCommit(next);
                  }}
                >
                  <Trash2 aria-hidden className="size-4" />
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      <Button
        type="button"
        variant="outline"
        size="sm"
        className="mt-2 border-ks-purple-3 bg-ks-surface text-ks-purple"
        onClick={() => onChange([...rows, blankIngredient(rows.length + 1)])}
      >
        <Plus aria-hidden className="size-4" />
        Add ingredient
      </Button>

      <p id={helpId} className="pt-1 text-sm text-ks-grey-1">
        {error ? (
          <span role="alert" className="text-ks-danger">
            {error}
          </span>
        ) : (
          help
        )}
      </p>
    </div>
  );
}
